import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_ENABLED = "ca_voice_enabled_v1";   // "on" | "off" | null (no preguntado)
const KEY_GENDER = "ca_voice_gender_v1";     // "female" | "male"
const KEY_SPEED = "ca_voice_speed_v1";

const VoiceCtx = createContext(null);

const cleanText = (t) => (t || "")
  .replace(/[*#_`>]+/g, "")
  .replace(/https?:\/\/\S+/g, "el enlace")
  .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B50}\u{2705}\u{274C}\u{2728}\u{FE0F}]/gu, "")
  .replace(/—\s*Equipo CrisAngel\.?\s*$/, "")
  .replace(/\s{2,}/g, " ")
  .trim();

const pickBrowserVoice = () => {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  return voices.find((v) => /es-MX/i.test(v.lang)) || voices.find((v) => /^es/i.test(v.lang)) || null;
};

export const VoiceAssistantProvider = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [enabled, setEnabledState] = useState(() => { try { return localStorage.getItem(KEY_ENABLED); } catch { return null; } });
  const [gender, setGenderState] = useState(() => { try { return localStorage.getItem(KEY_GENDER) || "female"; } catch { return "female"; } });
  const [speed, setSpeedState] = useState(() => { try { return Number(localStorage.getItem(KEY_SPEED)) || 1; } catch { return 1; } });
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [lastText, setLastText] = useState("");
  const audioRef = useRef(null);
  const unlockedRef = useRef(false);
  const queueRef = useRef([]);
  const playingRef = useRef(false);
  const urlCache = useRef(new Map());
  const recRef = useRef(null);
  const stickyRef = useRef(false);
  const prefRef = useRef({ gender, speed });
  useEffect(() => { prefRef.current = { gender, speed }; }, [gender, speed]);

  const isAdmin = user?.role === "admin";
  const isOn = enabled === "on" && !isAdmin;

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "auto";
      a.setAttribute("playsinline", "true");
      audioRef.current = a;
    }
    return audioRef.current;
  }, []);

  // Desbloqueo de audio en el primer gesto del usuario (iOS/Safari/Chrome)
  const unlock = useCallback(() => {
    if (unlockedRef.current) return;
    try {
      const a = getAudio();
      a.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYsQ6yoAAAAAAAAAAAAAAAAAAAA//tQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
      a.play().then(() => { a.pause(); unlockedRef.current = true; }).catch(() => { unlockedRef.current = true; });
      if (window.speechSynthesis) { const u = new SpeechSynthesisUtterance(""); window.speechSynthesis.speak(u); }
    } catch { /* noop */ }
  }, [getAudio]);

  useEffect(() => {
    const h = () => unlock();
    window.addEventListener("pointerdown", h, { once: true, capture: true });
    window.addEventListener("keydown", h, { once: true, capture: true });
    return () => { window.removeEventListener("pointerdown", h, { capture: true }); window.removeEventListener("keydown", h, { capture: true }); };
  }, [unlock]);

  const stop = useCallback(() => {
    queueRef.current = [];
    playingRef.current = false;
    try { const a = audioRef.current; if (a) { a.pause(); a.currentTime = 0; } } catch { /* noop */ }
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    setSpeaking(false);
  }, []);

  const speakBrowser = useCallback((text) => new Promise((resolve) => {
    if (!window.speechSynthesis) return resolve(false);
    const u = new SpeechSynthesisUtterance(text);
    const v = pickBrowserVoice();
    if (v) u.voice = v;
    u.lang = v?.lang || "es-MX";
    u.rate = speed;
    u.pitch = gender === "male" ? 0.8 : 1.05;
    u.onend = () => resolve(true);
    u.onerror = () => resolve(false);
    window.speechSynthesis.speak(u);
  }), [gender, speed]);

  const playOne = useCallback(async (text) => {
    const a = getAudio();
    const { gender, speed } = prefRef.current;
    const key = `${gender}|${speed}|${text}`;
    try {
      let url = urlCache.current.get(key);
      if (!url) {
        const r = await fetch(`${API}/voice/tts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, voice: gender, speed }) });
        if (!r.ok) throw new Error("tts");
        url = URL.createObjectURL(await r.blob());
        if (urlCache.current.size > 40) { const first = urlCache.current.keys().next().value; URL.revokeObjectURL(urlCache.current.get(first)); urlCache.current.delete(first); }
        urlCache.current.set(key, url);
      }
      await new Promise((resolve, reject) => {
        a.onended = () => resolve();
        a.onerror = () => reject(new Error("play"));
        a.src = url;
        a.play().catch(reject);
      });
      return true;
    } catch {
      return speakBrowser(text);
    }
  }, [getAudio, speakBrowser]);

  const pump = useCallback(async () => {
    if (playingRef.current) return;
    playingRef.current = true;
    setSpeaking(true);
    while (queueRef.current.length) {
      const next = queueRef.current.shift();
      setLastText(next);
      // eslint-disable-next-line no-await-in-loop
      await playOne(next);
      if (!playingRef.current) break;
    }
    playingRef.current = false;
    stickyRef.current = false;
    setSpeaking(false);
  }, [playOne]);

  /**
   * speak(text, { id, interrupt=true, once=true })
   * - id: evita repetir la misma guía (por sesión) si once=true
   * - interrupt: corta lo que se esté diciendo
   */
  const speak = useCallback((text, opts = {}) => {
    if (!isOn && !opts.force) return false;
    const clean = cleanText(text);
    if (!clean) return false;
    const { id, interrupt = true, once = true, sticky = false } = opts;
    if (sticky) stickyRef.current = true;
    if (id && once) {
      let seen = [];
      try { seen = JSON.parse(sessionStorage.getItem("ca_voice_spoken") || "[]"); } catch { seen = []; }
      if (seen.includes(id)) return false;
      try { sessionStorage.setItem("ca_voice_spoken", JSON.stringify([...seen.slice(-200), id])); } catch { /* noop */ }
    }
    if (interrupt) stop();
    queueRef.current.push(clean.slice(0, 1400));
    pump();
    return true;
  }, [isOn, stop, pump]);

  const setEnabled = useCallback((on) => {
    const v = on ? "on" : "off";
    try { localStorage.setItem(KEY_ENABLED, v); } catch { /* noop */ }
    setEnabledState(v);
    if (!on) stop();
    else unlock();
  }, [stop, unlock]);
  const setGender = useCallback((g) => { try { localStorage.setItem(KEY_GENDER, g); } catch { /* noop */ } prefRef.current = { ...prefRef.current, gender: g }; setGenderState(g); }, []);
  const setSpeed = useCallback((s) => { try { localStorage.setItem(KEY_SPEED, String(s)); } catch { /* noop */ } prefRef.current = { ...prefRef.current, speed: s }; setSpeedState(s); }, []);

  // Micrófono → Whisper
  const listen = useCallback(() => new Promise((resolve) => {
    if (!navigator.mediaDevices?.getUserMedia) return resolve({ error: "Tu navegador no permite usar el micrófono" });
    stop();
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1500) return resolve({ text: "" });
        const fd = new FormData();
        fd.append("file", blob, `voice.${(rec.mimeType || "webm").includes("mp4") ? "mp4" : "webm"}`);
        try {
          const r = await fetch(`${API}/voice/stt`, { method: "POST", body: fd });
          const d = await r.json();
          resolve({ text: d.text || "" });
        } catch { resolve({ error: "No pude entenderte, intenta de nuevo" }); }
      };
      recRef.current = rec;
      rec.start();
      setListening(true);
      setTimeout(() => { if (rec.state === "recording") rec.stop(); }, 15000);
    }).catch(() => resolve({ error: "Necesito permiso para usar el micrófono" }));
  }), [stop]);
  const stopListening = useCallback(() => { try { if (recRef.current?.state === "recording") recRef.current.stop(); } catch { /* noop */ } }, []);

  // Al cambiar de página, detener la voz
  useEffect(() => { if (!stickyRef.current) stop(); }, [pathname, stop]);

  const value = useMemo(() => ({
    enabled, isOn, asked: enabled !== null, isAdmin, gender, speed, speaking, listening, lastText,
    speak, stop, setEnabled, setGender, setSpeed, listen, stopListening, unlock,
  }), [enabled, isOn, isAdmin, gender, speed, speaking, listening, lastText, speak, stop, setEnabled, setGender, setSpeed, listen, stopListening, unlock]);

  return <VoiceCtx.Provider value={value}>{children}</VoiceCtx.Provider>;
};

export const useVoice = () => useContext(VoiceCtx) || {
  enabled: null, isOn: false, asked: true, isAdmin: false, gender: "female", speed: 1, speaking: false, listening: false, lastText: "",
  speak: () => false, stop: () => {}, setEnabled: () => {}, setGender: () => {}, setSpeed: () => {}, listen: async () => ({ text: "" }), stopListening: () => {}, unlock: () => {},
};

/** Lee una guía una sola vez cuando `active` es verdadero (por id). */
export const useVoiceGuide = (id, text, active = true, delay = 350, interrupt = false) => {
  const { speak, isOn } = useVoice();
  useEffect(() => {
    if (!isOn || !active || !text) return undefined;
    const t = setTimeout(() => {
      if (document.querySelector('[data-testid="tutorial-overlay"]')) return;
      speak(text, { id, interrupt });
    }, delay);
    return () => clearTimeout(t);
  }, [id, text, active, isOn, speak, delay, interrupt]);
};

export const voiceSessionId = () => {
  try {
    let id = localStorage.getItem("ca_voice_session");
    if (!id) { id = `v-${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem("ca_voice_session", id); }
    return id;
  } catch { return "visitor"; }
};
